import { useCallback, useContext } from "react";
import { useAutopilotContext } from "./useAutopilotContext";
import NavigationContext from "../contexts/NavigationContext";
import { AutopilotSessionRunningState } from "../__generated__/graphql";
import pluralize from "pluralize";

interface IUseAutopilotContactCountValidation {
  totalProfiles?: number | null;
  contactsScanned?: number | null;
  runningState?: AutopilotSessionRunningState | null;
}

type ValidationResult = { error: string | null; upgradeMessage: string | null };

export const useAutopilotContactCountValidation = ({
  totalProfiles,
  contactsScanned,
  runningState,
}: IUseAutopilotContactCountValidation = {}) => {
  const { maxNumberContactsPerAutopilotSearch, subscriptionTier } =
    useAutopilotContext();
  const { autopilotContactLimitDetails } = useContext(NavigationContext);

  // Derived values
  const remainingMonthlyContacts =
    autopilotContactLimitDetails?.remainingMonthlyContacts;

  const currentContactsInSearch = contactsScanned ?? 0;
  const isRunningMultisearch = Boolean(runningState?.multisearchDetail);
  const multisearchDetail = runningState?.multisearchDetail;
  const isFreeUser = subscriptionTier === "FREE";

  // Helpers
  const errorObj = (msg: string): ValidationResult => ({
    error: msg,
    upgradeMessage: null,
  });

  const validateContactCount = useCallback(
    (value: number): ValidationResult => {
      const upgradeObj = (
        msg: string,
        upgradeText: string,
      ): ValidationResult => ({
        error: msg,
        upgradeMessage: isFreeUser ? upgradeText : null,
      });

      // Data not loaded yet
      if (
        remainingMonthlyContacts === undefined ||
        remainingMonthlyContacts === null ||
        maxNumberContactsPerAutopilotSearch === null
      ) {
        return { error: null, upgradeMessage: null };
      }

      const buildMonthlyLimitError = (remaining: number): ValidationResult => {
        if (remaining > 0) {
          const msg = `Cannot analyze more than ${remaining} additional ${pluralize("profile", remaining)}. You have ${remaining} remaining ${pluralize("contact", remaining)} for the month.`;
          return upgradeObj(msg, "Upgrade to get more monthly contacts.");
        } else {
          const msg = "You have no more contacts remaining for the month.";
          return upgradeObj(msg, "Upgrade to get more monthly contacts.");
        }
      };

      if (!value || value <= 0) {
        return errorObj("Please enter a number greater than 0");
      }

      // Multisearch completion
      if (isRunningMultisearch && multisearchDetail) {
        const { countCompaniesSearched, totalCompaniesToSearch } =
          multisearchDetail;
        if (countCompaniesSearched >= totalCompaniesToSearch) {
          return errorObj(
            "All eligible companies have been scanned. Cannot continue search.",
          );
        }
      }

      // Total profiles available (for non-multisearch)
      if (
        totalProfiles != null &&
        contactsScanned != null &&
        !isRunningMultisearch
      ) {
        const remainingProfiles = totalProfiles - contactsScanned;
        if (value > remainingProfiles) {
          return errorObj(
            `Cannot review more than ${remainingProfiles} additional profiles. ${contactsScanned} of ${totalProfiles} eligible profiles already reviewed.`,
          );
        }
      }

      // Monthly contact limit (applies for both new and in-progress searches)
      if (value > remainingMonthlyContacts) {
        return buildMonthlyLimitError(remainingMonthlyContacts);
      }

      // Per-search limit (in-progress search)
      if (
        contactsScanned != null &&
        currentContactsInSearch + value > maxNumberContactsPerAutopilotSearch
      ) {
        const remainingInSearch =
          maxNumberContactsPerAutopilotSearch - currentContactsInSearch;
        const msg = `Cannot review more than ${remainingInSearch} additional profiles. The maximum number of contacts per search is ${maxNumberContactsPerAutopilotSearch}.`;
        return upgradeObj(msg, "Upgrade to analyze more contacts per search");
      }

      // For new searches, check against per-search limit
      if (contactsScanned == null) {
        if (value > maxNumberContactsPerAutopilotSearch) {
          const msg = `Please enter a number less than or equal to ${maxNumberContactsPerAutopilotSearch.toLocaleString()}`;
          return upgradeObj(msg, "Upgrade to analyze more contacts per search");
        }
      }

      return { error: null, upgradeMessage: null };
    },
    [
      isRunningMultisearch,
      multisearchDetail,
      totalProfiles,
      contactsScanned,
      remainingMonthlyContacts,
      currentContactsInSearch,
      maxNumberContactsPerAutopilotSearch,
      isFreeUser,
    ],
  );

  return {
    validateContactCount,
    remainingMonthlyContacts: Number.isFinite(remainingMonthlyContacts)
      ? remainingMonthlyContacts
      : 0,
  };
};
